import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const STORAGE_KEY = 'creator_tasks';
const MAX_TASKS = 50;
const POLL_INTERVAL = 5000;

function isByteDanceVideoModel(model) {
  if (typeof model !== 'string') return false;
  const normalized = model.trim().toLowerCase();
  return normalized.startsWith('zeelin-') || normalized.startsWith('doubao-seedance-');
}

function tryParseJson(value) {
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
}

function stringifyCreatorErrorDetail(value) {
  if (value == null) return '';
  if (typeof value === 'string') {
    const parsed = tryParseJson(value);
    return parsed ? JSON.stringify(parsed, null, 2) : value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
}

function findCreatorErrorMessage(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (value.error?.message) return value.error.message;
  if (value.error?.msg) return value.error.msg;
  if (typeof value.error === 'string' && value.error) return value.error;
  if (value.message) {
    const parsedMessage = tryParseJson(value.message);
    if (parsedMessage) {
      return findCreatorErrorMessage(parsedMessage) || value.message;
    }
    return value.message;
  }
  return '';
}

export function getCreatorTaskIdErrorMessage(responseBody) {
  return findCreatorErrorMessage(responseBody) || 'No task_id returned from the API';
}

export function getCreatorTaskErrorSummary(responseBody) {
  return findCreatorErrorMessage(responseBody) || '任务失败';
}

export function getCreatorTaskErrorDetail(responseBody) {
  if (!responseBody) return '';
  if (typeof responseBody?.message === 'string' && responseBody.message) {
    const parsedMessage = tryParseJson(responseBody.message);
    if (parsedMessage) {
      return stringifyCreatorErrorDetail(parsedMessage);
    }
  }
  return stringifyCreatorErrorDetail(responseBody);
}

export function buildCreatorRequestBody({
  model,
  prompt,
  options,
  mode,
  generateMode,
  firstFrameUrl,
  lastFrameUrl,
  referenceUrls,
  imageUrls,
  videoUrls,
  audioUrls,
}) {
  const uniqueUrls = (urls) =>
    Array.from(
      new Set((Array.isArray(urls) ? urls : []).filter((url) => typeof url === 'string' && url))
    );
  const useByteDanceByPass = mode === 'video' && isByteDanceVideoModel(model);
  const flatOptions = { ...(options || {}) };
  const byPass = {};
  Object.keys(flatOptions).forEach((key) => {
    if (key.startsWith('byPass.')) {
      const subKey = key.slice(7);
      const val = flatOptions[key];
      if (val === '' || (Array.isArray(val) && val.length === 0)) return;
      byPass[subKey] = val;
      delete flatOptions[key];
    }
  });

  const body = {
    model,
    prompt,
    ...flatOptions,
  };
  if (body.duration !== undefined) {
    const parsedDuration = Number(body.duration);
    if (!Number.isNaN(parsedDuration)) {
      body.duration = parsedDuration;
    }
  }
  if (Object.keys(byPass).length > 0) {
    body.byPass = byPass;
  }

  if (mode === 'video') {
    if (typeof generateMode === 'number') {
      body.generate_mode = generateMode;
    }

    const normalizedReferenceUrls = uniqueUrls(referenceUrls || imageUrls);
    const normalizedImageUrls = uniqueUrls(imageUrls || referenceUrls);
    const normalizedVideoUrls = uniqueUrls(videoUrls);
    const normalizedAudioUrls = uniqueUrls(audioUrls);
    const images = [];
    if (firstFrameUrl) {
      images.push(firstFrameUrl);
    }
    if (lastFrameUrl) {
      images.push(lastFrameUrl);
    }
    if (!lastFrameUrl) {
      if (useByteDanceByPass && normalizedImageUrls.length > 0) {
        images.push(...normalizedImageUrls);
      } else if (!useByteDanceByPass && normalizedReferenceUrls.length > 0) {
        images.push(...normalizedReferenceUrls);
      }
    }
    if (images.length > 0) {
      body.images = images;
    }
    if (useByteDanceByPass && normalizedVideoUrls.length > 0) {
      byPass.videos = normalizedVideoUrls;
    }
    if (useByteDanceByPass && normalizedAudioUrls.length > 0) {
      byPass.audios = normalizedAudioUrls;
    }
  }

  if (mode === 'image') {
    const normalizedReferenceUrls = uniqueUrls(referenceUrls || imageUrls);
    if (normalizedReferenceUrls.length > 0) {
      body.images = normalizedReferenceUrls;
    }
  }

  if (mode !== 'video' || !useByteDanceByPass) {
    const normalizedAudioUrls = uniqueUrls(audioUrls);
    if (normalizedAudioUrls.length > 0) {
      body.audios = normalizedAudioUrls;
    }
  }

  if (Object.keys(byPass).length > 0) {
    body.byPass = {
      ...(body.byPass || {}),
      ...byPass,
    };
  }

  return body;
}

function loadTasks() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // ignore
  }
  return [];
}

function saveTasks(tasks) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks.slice(0, MAX_TASKS)));
  } catch (e) {
    // ignore
  }
}

export default function useTaskManager() {
  const [tasks, setTasks] = useState(() => loadTasks());
  const pollTimers = useRef({});

  useEffect(() => {
    saveTasks(tasks);
  }, [tasks]);

  useEffect(() => {
    const loaded = loadTasks();
    loaded.forEach((task) => {
      if (task.status === 'processing') {
        startPolling(task.id, task.apiKey, task.queryEndpoint, task.model);
      }
    });
    const timers = pollTimers.current;
    return () => {
      Object.keys(timers).forEach((key) => {
        clearInterval(timers[key]);
        delete timers[key];
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback((taskId, apiKey, queryEndpoint, model) => {
    if (pollTimers.current[taskId]) return;

    let errorCount = 0;
    const MAX_ERRORS = 10;

    const stopPolling = (timer) => {
      clearInterval(timer);
      delete pollTimers.current[taskId];
    };

    const markFailed = (timer, errorMsg, errorDetail = '', result = null) => {
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? {
                ...t,
                status: 'failed',
                error: errorMsg,
                errorDetail,
                result: result || t.result,
                completedAt: Date.now(),
              }
            : t
        )
      );
      stopPolling(timer);
    };

    const timer = setInterval(async () => {
      try {
        const res = await axios.post('/api/creator/execute', {
          endpoint: queryEndpoint || '/v1/video/result',
          method: 'POST',
          body: { model, task_id: taskId },
          api_key: apiKey,
        });

        if (!res.data.success) {
          errorCount++;
          console.error('Polling proxy error for task', taskId, res.data.message);
          if (errorCount >= MAX_ERRORS) {
            markFailed(timer, res.data.message || '查询任务失败，已达最大重试次数');
          }
          return;
        }

        errorCount = 0;

        const body = res.data.data?.body;
        const statusCode = res.data.data?.status_code;

        if (statusCode >= 400) {
          markFailed(
            timer,
            getCreatorTaskErrorSummary(body) || `上游返回错误 (${statusCode})`,
            getCreatorTaskErrorDetail(body),
            body
          );
          return;
        }

        if (
          body?.status === 'SUCCESS' ||
          body?.status === 'success' ||
          (body?.data && Array.isArray(body.data) && body.data.length > 0)
        ) {
          let videoUrl = null;
          if (body.data && Array.isArray(body.data) && body.data.length > 0) {
            videoUrl = body.data[0].url || body.data[0].video_url || null;
          }

          setTasks((prev) =>
            prev.map((t) =>
              t.id === taskId
                ? { ...t, status: 'completed', videoUrl, result: body, completedAt: Date.now() }
                : t
            )
          );
          stopPolling(timer);
          return;
        }

        if (body?.status === 'FAILED' || body?.status === 'failed' || body?.error) {
          markFailed(timer, getCreatorTaskErrorSummary(body), getCreatorTaskErrorDetail(body), body);
          return;
        }
      } catch (err) {
        errorCount++;
        console.error('Polling network error for task', taskId, err);
        if (errorCount >= MAX_ERRORS) {
          markFailed(timer, '网络错误，已达最大重试次数');
        }
      }
    }, POLL_INTERVAL);

    pollTimers.current[taskId] = timer;
  }, []);

  const addTask = useCallback(
    async ({
      prompt,
      model,
      options,
      apiKey,
      endpoint,
      queryEndpoint,
      mode,
      generateMode,
      firstFrameUrl,
      lastFrameUrl,
      referenceUrls,
      imageUrls,
      videoUrls,
      audioUrls,
    }) => {
      if (mode === 'chat') {
        const body = {
          model,
          messages: [{ role: 'user', content: prompt }],
        };

        const res = await axios.post('/api/creator/execute', {
          endpoint: endpoint || '/v1/chat/completions',
          method: 'POST',
          body,
          api_key: apiKey,
        });

        if (!res.data.success) {
          throw new Error(res.data.message || 'Request failed');
        }

        const responseBody = res.data.data?.body;
        const reply =
          responseBody?.choices?.[0]?.message?.content || JSON.stringify(responseBody);

        const newTask = {
          id: 'chat-' + Date.now(),
          prompt,
          model,
          mode,
          options,
          status: 'completed',
          result: responseBody,
          chatReply: reply,
          error: null,
          createdAt: Date.now(),
          completedAt: Date.now(),
        };

        setTasks((prev) => [newTask, ...prev].slice(0, MAX_TASKS));
        return newTask;
      }

      const body = buildCreatorRequestBody({
        model,
        prompt,
        options,
        mode,
        generateMode,
        firstFrameUrl,
        lastFrameUrl,
        referenceUrls,
        imageUrls,
        videoUrls,
        audioUrls,
      });

      const res = await axios.post('/api/creator/execute', {
        endpoint: endpoint || '/v1/video/generations',
        method: 'POST',
        body,
        api_key: apiKey,
      });

      if (!res.data.success) {
        throw new Error(res.data.message || 'Request failed');
      }

      const responseBody = res.data.data?.body;

      if (mode === 'image') {
        let imageUrl = null;
        if (responseBody?.data && Array.isArray(responseBody.data) && responseBody.data.length > 0) {
          imageUrl = responseBody.data[0].url || responseBody.data[0].b64_json || null;
        }

        const newTask = {
          id: 'img-' + Date.now(),
          prompt,
          model,
          mode,
          options,
          status: imageUrl ? 'completed' : 'failed',
          imageUrl,
          result: responseBody,
          error: imageUrl ? null : '未返回图片',
          createdAt: Date.now(),
          completedAt: Date.now(),
        };

        setTasks((prev) => [newTask, ...prev].slice(0, MAX_TASKS));
        return newTask;
      }

      const taskId = responseBody?.task_id;

      if (!taskId) {
        throw new Error(getCreatorTaskIdErrorMessage(responseBody));
      }

      const newTask = {
        id: taskId,
        prompt,
        model,
        mode,
        options,
        apiKey,
        queryEndpoint: queryEndpoint || '/v1/video/result',
        status: 'processing',
        videoUrl: null,
        result: null,
        error: null,
        createdAt: Date.now(),
        completedAt: null,
      };

      setTasks((prev) => [newTask, ...prev].slice(0, MAX_TASKS));

      startPolling(taskId, apiKey, queryEndpoint, model);

      return newTask;
    },
    [startPolling]
  );

  const removeTask = useCallback((taskId) => {
    if (pollTimers.current[taskId]) {
      clearInterval(pollTimers.current[taskId]);
      delete pollTimers.current[taskId];
    }
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, []);

  const clearCompletedTasks = useCallback(() => {
    setTasks((prev) => {
      prev.forEach((t) => {
        if (t.status !== 'processing' && pollTimers.current[t.id]) {
          clearInterval(pollTimers.current[t.id]);
          delete pollTimers.current[t.id];
        }
      });
      return prev.filter((t) => t.status === 'processing');
    });
  }, []);

  return { tasks, addTask, removeTask, clearCompletedTasks };
}
